import { OptionSelect } from "Common/Components";
import { useSessionStore } from "Common/Stores";
import { TFunction } from "i18next";
import * as Yup from "yup";

export enum userType {
  adminitrador = "administrador",
  empresa = "empresa",
  sucursal = "sucursal",
}

export const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
};

/**
 * @description This function validate if the user type in session is permited
 * @param {userType[]} usersPermited - Array of user types permited
 * @returns {boolean} - Return true if the user type in session is permited
 * @example validateUserTypePermited([userType.adminitrador])
 * @example validateUserTypePermited([userType.adminitrador, userType.empresa])
 */

export const validateUserTypePermited = (usersPermited: userType[]) => {
  const session = useSessionStore.getState().session;

  if (!session) return false;

  const userTypeInSession = session.user.userType.description;

  return usersPermited.includes(userTypeInSession as userType);
};

/**
 * @description This function return the default value for select component
 * @param {OptionSelect[]} options - Array of options
 * @param {string} value - Value to compare
 * @returns {OptionSelect} - Return the default value of type OptionsSelect for select component
 * @example getDefaultValueForSelect(options, value)
 */

export const getDefaultValueForSelect = (
  options: OptionSelect[],
  value: string
) => options.find((option) => option.value === value);

/**
 * Validate a string with special characters
 * @param t Translation function
 * @param admitedNumbers If true, numbers are allowed
 * @returns Yup.validateMethod<string>
 * @example
 * validateYupStringWithSpecialCharacters(t)
 *
 *
 *
 **/
const validateYupStringWithSpecialCharacters = (
  t: TFunction<"translation", undefined>,
  admitedNumbers: boolean = false
) => {
  const regex = admitedNumbers ? /^[A-Za-z0-9ñÑ\s]+$/ : /^[A-Za-zñÑ\s]+$/;

  return Yup.string()
    .test("no-leading-spaces", t("validationForms.noLeadingSpaces"), (val) => {
      if (val) {
        return !/^\s/.test(val);
      }
      return true;
    })
    .test("only-letters", t("validationForms.onlyLetters"), (val) => {
      if (val) {
        return regex.test(val);
      }
      return true;
    })
    .required(t("validationForms.required"));
};

const validateYupAddress = (
  t: TFunction<"translation", undefined>,
  admitedNumbers: boolean = false
) => {
  const regex = admitedNumbers ? /^[A-Za-z0-9ñÑ\s-#]+$/ : /^[A-Za-z9123456780ñÑ\s-#]+$/;

  return Yup.string()
    .test("no-leading-spaces", t("validationForms.noLeadingSpaces"), (val) => {
      if (val) {
        return !/^\s/.test(val);
      }
      return true;
    })
    .test(
      "only-letters",
      t("validationForms.onlyLetterWhitCaracter"),
      (val) => {
        if (val) {
          return regex.test(val);
        }
        return true;
      }
    )
    .required(t("validationForms.required"));
};

const validateYupStringWithMinLegthMaxLength = (
  t: TFunction<"translation", undefined>,
  minLength: number = 3,
  maxLength: number = 100
) => {
  return Yup.string()
    .required(t("validationForms.required"))
    .trim()
    .min(minLength, t("validationForms.minLength", { minLength: minLength }))
    .max(maxLength, t("validationForms.maxLength", { maxLength: maxLength }))
    .matches(
      /^[A-Za-zÁÉÍÓÚáéíóúÜüÑñ0-9-., ]+$/,
      t("validationForms.specialChars")
    )
    .test(
      "no-only-spaces",
      t("validationForms.noOnlySpaces"),
      (value) => value?.trim().length > 0
    );
};

const validatePhoneNumber = (t: TFunction<"translation", undefined>) => {
  return Yup.string()
    .required(t("validationForms.required"))
    .matches(/^\d{8,12}$/, t("validationForms.phone"))
    .matches(/^[0-9]+$/, t("validationForms.onlyNumbers"));
};

const validateNit = (t: TFunction<"translation", undefined>) => {
  return Yup.string()
    .required(t("validationForms.required"))
    .matches(/^\d{8,12}$/, t("validationForms.nit"))
    .matches(/^[0-9]+$/, t("validationForms.onlyNumbers"));
};

const validateSelect = (t: TFunction<"translation", undefined>) => {
  return Yup.number()
    .required(t("validationForms.required"))
    .min(1, t("validationForms.select"))
    .typeError(t("validationForms.select"));
};

const validateEmail = (t: TFunction<"translation", undefined>) => {
  return Yup.string()
    .required(t("validationForms.required"))
    .email(t("validationForms.email"));
};

export const validateYupCodeWithMinMaxLeght = (
  t: TFunction<"translation", undefined>,
  minLength: number = 5,
  maxLength: number = 5,
  type: "letters" | "numbers" | "both" = "both"
) => {
  const pattern = {
    letters: /^[A-Za-z]+$/,
    numbers: /^[0-9]+$/,
    both: /^[A-Za-z0-9]+$/,
  }[type];

  const errorMessage = {
    letters: t("validationForms.onlyLetters"),
    numbers: t("validationForms.onlyNumbers"),
    both: t("validationForms.lettersAndNumbers"),
  }[type];

  return Yup.string()
    .required(t("validationForms.required"))
    .trim()
    .min(minLength, t("validationForms.minLength", { minLength: minLength }))
    .max(maxLength, t("validationForms.maxLength", { maxLength: maxLength }))
    .matches(pattern, errorMessage)
    .test(
      "no-only-spaces",
      t("validationForms.noOnlySpaces"),
      (value) => value?.trim().length > 0
    );
};

/**
 *
 * @param t Traslaction function
 * @param admitedDecimales If true, numbers with decimals are allowed
 * @returns Yup.validateMethod<string>
 * @example validateOnlyNumbers(t)
 *
 */
const validateOnlyNumbers = (
  t: TFunction<"translation">,
  admitedDecimales: boolean = false
) => {
  const regex = admitedDecimales ? /^[0-9]+(\.[0-9]{1,2})?$/ : /^[0-9]+$/;

  return Yup.string()
    .required(t("validationForms.required"))
    .matches(regex, t("validationForms.onlyNumbers"));
};

const validateUrlPath = (t: TFunction<"translation", undefined>) => {
  const regex = /^(\/[a-zA-ZñÑ00-9-_/-]+)+$/;

  return Yup.string()
    .required(t("validationForms.required"))
    .matches(regex, t("validationForms.lettersAndNumbers"))
    .test(
      "no-only-spaces",
      t("validationForms.noOnlySpaces"),
      (value) => value?.trim().length > 0
    );
};

/**
 * Format the price in MXN
 * @param price Price
 * @returns string Price in format $0.00
 * @example formatPrice(100) // "$100.00"
 */
const formatPrice = (price: number) => {
  return price.toLocaleString("es-GT", {
    style: "currency",
    currency: "GTQ",
  });
};

export {
  formatPrice,
  validateOnlyNumbers,
  validateSelect,
  validatePhoneNumber,
  validateYupStringWithMinLegthMaxLength,
  validateYupStringWithSpecialCharacters,
  validateNit,
  validateEmail,
  validateYupAddress,
  validateUrlPath,
};
